import { ReactElement, ReactNode } from 'react';

import useFeature from '~/store/clinic/hooks/useFeature';
import { useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { useSignalRToken } from '~/services/socket/SignalRTokenProvider';
import { SignalRCrisisContext } from '~/ui/hooks/useCrisisSignalR';

interface IProps {
  children: ReactNode | ReactNode[];
}

const CrisisRequestsProvider = ({ children }: IProps): ReactElement => {
  const { token, isOnline, handleError, handleConnection } = useSignalRToken();
  const { isLocalAdmin } = useRole();
  const { crisisAccess } = useStoreState(state => state.user.current) || {};
  const { isCrisisAvailable, isCrisisAlertsAvailable } = useFeature();

  const isConnectionEnabled =
    Boolean(isLocalAdmin) && isCrisisAvailable && isCrisisAlertsAvailable && crisisAccess;

  const isConnected = !!token && isConnectionEnabled && isOnline;

  const connectionCloseDelay = () =>
    new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });

  return (
    <SignalRCrisisContext.Provider
      connectEnabled={isConnected}
      withCredentials={false}
      dependencies={[token]}
      transport={1}
      skipNegotiation
      url={process.env.REACT_APP_API_URL.replace(
        'api/v1/',
        `hubs/crisis-map?access_token=${token}`,
      )}
      logger={6}
      onBeforeClose={connectionCloseDelay}
      onOpen={handleConnection}
      onReconnect={handleConnection}
      onError={handleError}
    >
      {children}
    </SignalRCrisisContext.Provider>
  );
};

export default CrisisRequestsProvider;
